import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_NAME, APP_VERSION } from './app-version';
import { PhotoUploadService } from './photo-upload.service';

interface VersionInfo {
  app?: string;
  version?: string;
  buildTime?: string;
  forceUpdate?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private http = inject(HttpClient);
  private readonly photoUploads = inject(PhotoUploadService);
  private intervalId: number | null = null;
  private checkInFlight = false;

  readonly currentVersion = APP_VERSION;
  readonly latestVersion = signal('');
  readonly forceUpdate = signal(false);
  readonly updateAvailable = signal(false);
  readonly updating = signal(false);
  readonly clearingCache = signal(false);

  start() {
    this.check();
    if (this.intervalId === null) {
      this.intervalId = window.setInterval(() => this.check(), 5 * 60 * 1000);
    }
  }

  check() {
    if (this.checkInFlight) return;
    this.checkInFlight = true;
    this.http.get<VersionInfo>(`/version.json?_=${Date.now()}`).subscribe({
      next: info => {
        const latest = info?.version || '';
        const appMatches = !info?.app || info.app === APP_NAME;
        this.latestVersion.set(latest);
        this.forceUpdate.set(!!info?.forceUpdate);
        this.updateAvailable.set(!!latest && appMatches && latest !== APP_VERSION);
        this.checkInFlight = false;
      },
      error: () => {
        this.checkInFlight = false;
      }
    });
  }

  async reload() {
    if (!this.canRefresh()) return;
    if (this.updating() || this.clearingCache()) return;
    this.updating.set(true);
    this.updateAvailable.set(false);

    try {
      if ('serviceWorker' in navigator) {
        const activation = this.waitForControllerChange(4000);
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.update()));

        for (const registration of registrations) {
          if (registration.installing) {
            await this.waitForWorkerInstall(registration.installing, 4000);
          }
          registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
        }

        if (registrations.length) await activation;
      }
    } catch (err) {
      console.warn('Service worker update check failed', err);
    } finally {
      const url = new URL(window.location.href);
      url.searchParams.set('update', Date.now().toString());
      window.location.replace(url.toString());
    }
  }

  async clearCacheAndRefresh() {
    if (!this.canRefresh()) return;
    if (this.clearingCache()) return;
    this.clearingCache.set(true);
    this.updateAvailable.set(false);

    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.unregister()));
      }
    } catch (error) {
      console.warn('App cache cleanup did not fully complete', error);
    } finally {
      const url = new URL(window.location.href);
      url.searchParams.set('refresh', Date.now().toString());
      window.location.replace(url.toString());
    }
  }

  private canRefresh(): boolean {
    if (!this.photoUploads.unsafeToReload()) return true;
    window.alert('Photos are being saved to this device. Please wait a moment before refreshing.');
    return false;
  }

  private waitForControllerChange(timeoutMs: number): Promise<void> {
    return new Promise(resolve => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        resolve();
      };
      const timeoutId = window.setTimeout(finish, timeoutMs);
      navigator.serviceWorker.addEventListener('controllerchange', finish, { once: true });
    });
  }

  private waitForWorkerInstall(worker: ServiceWorker, timeoutMs: number): Promise<void> {
    if (worker.state === 'installed' || worker.state === 'activated' || worker.state === 'redundant') {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        worker.removeEventListener('statechange', onStateChange);
        resolve();
      };
      const onStateChange = () => {
        if (worker.state === 'installed' || worker.state === 'activated' || worker.state === 'redundant') finish();
      };
      const timeoutId = window.setTimeout(finish, timeoutMs);
      worker.addEventListener('statechange', onStateChange);
    });
  }
}
